import first from "lodash/first";
import sortBy from "lodash/sortBy";
import * as React from "react";
import { RouteComponentProps } from "react-router";
import { Redirect } from "react-router-dom";
import { LoadingSpinner } from "@app/shared";
import { CodeMapSet } from "@app/codemaps";
import { ClientInsuranceModel } from "@app/insurance";
import { MemberSetModel } from "@app/members";
import { QESAlert, QESButton, QESCheckbox, QESSelect } from "@app/shared/qes-standards";
import { AccuracySpecialtyDefinition, AccuracyTemplateCollectionMetadata, TemplateCollectionMetadata, TemplateService, TemplateSpecialtyGroup } from "@app/templates";
import { RoleRestricted, Roles, UserContextProps } from "@app/user";
import { TimeDistanceCalculatorType } from "../enumerators";
import CreateProject from "./createProject";
import SpecialtyGroupsTable from "./SpecialtyGroupsTable";
import { InsuranceList } from "../../insurance/InsuranceList";
import { timeDistanceCalculatorTypeDisplayMap } from "../timeDistanceCalculatorTypeDisplayMap";
import WizardStep from "./WizardStep";
import { JSX } from "react";

interface CreateProjectStepTemplateProps extends RouteComponentProps {
  accuracyIsLoading: boolean;
  accuracyTemplateCollections: AccuracyTemplateCollectionMetadata[];
  accuracySpecialtyDefinitions: AccuracySpecialtyDefinition[];
  authorizedInsurances: ClientInsuranceModel[];
  authorizedInsurancesErred: boolean;
  baseUrl: string;
  completionTracker: boolean[];
  createProjectModel: CreateProject;
  handleAccessCodeMapSetChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleAccuracyTemplateCollectionsChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleAccuracyTemplateCorrelationIdsChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleAccuracySpecialtyDefChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleAddInspectedInsurance: () => void;
  handleAuthorizedInsurancesChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleMarketSpecialtyCodeMapSetChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleNetworkSpecialtyCodeMapSetChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleRemoveInspectedInsurance: (uid: string) => void;
  handleSpecialtyGroupsChange: (selectedRecords: TemplateSpecialtyGroup[]) => void;
  handleSpecialtyToQdpCodeMapSetChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleTelehealthSpecialtyGroupsChange: (selectedSpecialtyGroupCode: string[]) => void;
  handleTemplateCollectionsChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleTemplateCorrelationIdsChange: (e: React.ChangeEvent<HTMLSelectElement>, userRoles: string[]) => void;
  handleTimeDistanceCalculationTypeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleUseAccuracyChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleUseAdequacyChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isZipProject: boolean;
  originalTemplateCollections: TemplateCollectionMetadata[];
  progressList: string[];
  projectAccessCodeMapSets: CodeMapSet[];
  projectCollections: TemplateCollectionMetadata[];
  projectTemplateCollections: TemplateCollectionMetadata[];
  selectedInsurances: ClientInsuranceModel[];
  selectedInsuranceUid: string;
  showNetworkSpecialtyCodeMap: boolean;
  specialtyCodeMapSets: CodeMapSet[];
  specialtyDefinitionsMissing: boolean;
  specialtyGroups: TemplateSpecialtyGroup[];
  specialtyToQdpCodeMapSets: CodeMapSet[];
  timeDistanceCalculatorTypes: TimeDistanceCalculatorType[];
  useAccuracy: boolean;
  useAdequacy: boolean;
  accessCodeMapDefaultSetId: number;
  currentIndex: number;
}

interface CreateProjectStepTemplateState {
  sortedSpecialtyGroups: TemplateSpecialtyGroup[];
  restartApp: boolean;
}

export default class CreateProjectStepTemplate extends React.Component<CreateProjectStepTemplateProps & UserContextProps, CreateProjectStepTemplateState> {
  private readonly sortOrder: Array<keyof TemplateSpecialtyGroup> = ["displayOrder"];
  private readonly templateService = new TemplateService();
  private priorityTemplates: string[] = [];

  constructor(props: CreateProjectStepTemplateProps & UserContextProps) {
    super(props);
    this.state = {
      restartApp: !props.completionTracker.filter((_, index) => (props.currentIndex - 1) > index).every(step => step),
      sortedSpecialtyGroups: props.specialtyGroups && sortBy(props.specialtyGroups, this.sortOrder) || props.specialtyGroups,
    };
  }

  public componentDidUpdate(prevProps: CreateProjectStepTemplateProps & UserContextProps) {
    if (this.props.specialtyGroups !== prevProps.specialtyGroups) {
      this.setState({
        sortedSpecialtyGroups: this.props.specialtyGroups && sortBy(this.props.specialtyGroups, this.sortOrder) || this.props.specialtyGroups,
      });
    }
  }

  public async componentDidMount() {
    this.priorityTemplates = await this.templateService.getPriorityCmsTemplates();
  }

  public render() {
    const calculatorTypeRolesMap: { [key: string]: Roles[] } = {
      [TimeDistanceCalculatorType.Unknown]: undefined,
      [TimeDistanceCalculatorType.Quest]: undefined,
      [TimeDistanceCalculatorType.GeographyAware]: [Roles.GEOGRAPHY_AWARE_DISTANCE_USER],
      [TimeDistanceCalculatorType.GraphhopperRouting]: [Roles.ROUTING_DISTANCE_USER],
      [TimeDistanceCalculatorType.StraightLine]: [Roles.STRAIGHT_LINE_DISTANCE_USER],
    };

    if (this.state.restartApp) {
      return <Redirect to={`${this.props.baseUrl}/1`} />;
    }

    const sortedTimeDistanceCalculatorTypes = this.props.timeDistanceCalculatorTypes.sort(
      (a, b) => timeDistanceCalculatorTypeDisplayMap[a].localeCompare(timeDistanceCalculatorTypeDisplayMap[b]));

    const timeDistanceCalculatorTypeDefault = (this.props.createProjectModel && this.props.createProjectModel.timeDistanceCalculatorType)
      ? this.props.createProjectModel.timeDistanceCalculatorType.toString()
      : sortedTimeDistanceCalculatorTypes.length > 0
        ? sortedTimeDistanceCalculatorTypes.find(t => t === TimeDistanceCalculatorType.Quest) ?? sortedTimeDistanceCalculatorTypes[0]
        : TimeDistanceCalculatorType.Unknown;

    const projectTemplateCollectionDefault = (this.props.createProjectModel && this.props.createProjectModel.adequacyTemplateCollectionId)
      ? this.props.createProjectModel.adequacyTemplateCollectionId.toString()
      : 0;
    const projectTemplateCorrelationIdDefault =
      (this.props.createProjectModel && this.props.createProjectModel.adequacyTemplateCorrelationId)
        ? this.props.createProjectModel.adequacyTemplateCorrelationId
        : 0;

    let renderProjectTemplateCorrelationIdsList: JSX.Element;
    let renderAccessCodeMapsList: JSX.Element;
    let renderNetworkSpecialtyCodeMapsList: JSX.Element;
    let renderMarketSpecialtyCodeMapsList: JSX.Element;

    const renderTimeDistanceCalculationTypeList: JSX.Element = (
      <QESSelect
        label="Time/Distance Calculation Type"
        className="form-control"
        value={timeDistanceCalculatorTypeDefault}
        testId="create-project-step-template__adequacy-time-distance-calculation-type-list-select"
        onChange={this.props.handleTimeDistanceCalculationTypeChange}
      >
        {sortedTimeDistanceCalculatorTypes.map((option) => calculatorTypeRolesMap[option] ? (<RoleRestricted key={option} allowedRoles={calculatorTypeRolesMap[option]}><option key={option} value={option}>{timeDistanceCalculatorTypeDisplayMap[option]}</option></RoleRestricted>) : (<option key={option} value={option}>{timeDistanceCalculatorTypeDisplayMap[option]}</option>))}
      </QESSelect>
    );

    const renderProjectTemplateCollectionsList = (
      <QESSelect
        label="Adequacy Template Collection"
        className="form-control"
        value={projectTemplateCollectionDefault}
        testId="create-project-step-template__adequacy-template-collections-list-select"
        onChange={this.props.handleTemplateCollectionsChange}
      >
        <option value="0">Select an Adequacy Template Collection</option>
        {this.props.projectCollections?.sort((a, b) => a.resource.name > b.resource.name ? 1 : -1).map((option) => (
          <option key={option.resource.resourceId} value={option.resource.resourceId}>{`${option.resource.name} - ${option.resource.description}`}</option>
        ))}
      </QESSelect>
    );

    if (this.props.createProjectModel.adequacyTemplateCollectionId) {
      const selectedProjectCollection = first(this.props.projectCollections.filter((item) => item.templateCollectionId === this.props.createProjectModel.adequacyTemplateCollectionId));
      const priorityTemplatesToInclude = this.priorityTemplates.filter((pt) => selectedProjectCollection?.correlationIds.includes(pt)) || [];
      const sortedNonPriorityTemplates = selectedProjectCollection?.correlationIds.sort().filter((t) => !this.priorityTemplates.includes(t)) || [];

      renderProjectTemplateCorrelationIdsList = (
        <QESSelect
          label="Adequacy Template"
          className="form-control"
          value={projectTemplateCorrelationIdDefault}
          testId="create-project-step-template__adequacy-template-correlation-ids-list-select"
          onChange={(e) => this.props.handleTemplateCorrelationIdsChange(e, this.props.user.roles)}
        >
          <option value="0">Select an Adequacy Template</option>
          {priorityTemplatesToInclude.length > 0 && (
            <optgroup label="New Templates">
              {
                priorityTemplatesToInclude.map((t) => (
                  <option key={t} value={t}>{`${t}`}</option>
                ))
              }
            </optgroup>
          )}
          {sortedNonPriorityTemplates.map((option) => (
            <option key={option} value={option}>{`${option}`}</option>
          ))}
        </QESSelect>
      );
    } else {
      renderProjectTemplateCorrelationIdsList = (<QESSelect label="Adequacy Template" testId="Loading" className="form-control"><option key="0" value="0">Select an Adequacy Template</option></QESSelect>);
    }

    //==========
    // Accuracy
    //==========
    let renderAccuracyTemplateCollectionsList: React.ReactNode;
    let renderAccuracyTemplateCorrelationIdsList: React.ReactNode;
    let renderAccuracySpecialtyDefinitionList: React.ReactNode;
    let renderAuthorizedInsurancesList: React.ReactNode;

    const hasAccuracyTemplates = this.props.accuracyTemplateCollections && this.props.accuracyTemplateCollections.length > 0;
    if (this.props.useAccuracy && hasAccuracyTemplates) {
      const getOptionValue = (option: AccuracyTemplateCollectionMetadata) => option.resource && option.resource.name || option.templateCollectionId;
      renderAccuracyTemplateCollectionsList = (
        <QESSelect
          label="Accuracy Template Collection"
          className="form-control"
          value={this.props.createProjectModel.accuracyTemplateCollectionId || ""}
          testId="create-project-step-template__accuracy-template-collections-list-select"
          onChange={this.props.handleAccuracyTemplateCollectionsChange}
        >
          <option key="<none>" value="">Select an Accuracy Template Collection</option>
          {this.props.accuracyTemplateCollections.sort((a, b) => getOptionValue(a) > getOptionValue(b) ? 1 : -1).map(
            (option) => (
              <option key={option.templateCollectionId} value={option.templateCollectionId}>{getOptionValue(option)
              }</option>
            ))}
        </QESSelect>
      );

      renderAuthorizedInsurancesList = (
        <>
          <div className="d-flex justify-content-between align-items-end">
            <QESSelect
              label="Inspected Insurances"
              containerClassName="w-100"
              testId="create-project-step-template__inspected-insurances"
              className="form-control"
              onChange={this.props.handleAuthorizedInsurancesChange}
            >
              <option key="<none>" value="">Select an insurance</option>
              {this.props.authorizedInsurances.filter((ins) =>
                this.props.selectedInsurances.findIndex((lookup) => lookup.uid === ins.uid) === -1).map(
                  (option) => (
                    <option key={option.uid} value={option.uid}>{option.planName}: {option.uid}</option>))}
            </QESSelect>
            <QESButton qesTestId="create-project-step-template_plus-icon" qesType="primary" onClick={() => this.props.handleAddInspectedInsurance()}><i className="fa fa-plus" /></QESButton>
          </div>
          <InsuranceList selectedInsurances={this.props.selectedInsurances} handleRemoveInspectedInsurance={this.props.handleRemoveInspectedInsurance} />
        </>
      );

      const selectedTemplateCollection = this.props.accuracyTemplateCollections.find((t) => t.templateCollectionId === this.props.createProjectModel.accuracyTemplateCollectionId);
      const correlationIds = (selectedTemplateCollection && selectedTemplateCollection.correlationIds) || [];

      renderAccuracyTemplateCorrelationIdsList = (
        <QESSelect
          label="Accuracy Template"
          className="form-control"
          value={this.props.createProjectModel.accuracyTemplateCorrelationId || ""}
          testId="create-project-step-template__accuracy-template-correlation-ids-list-select"
          onChange={this.props.handleAccuracyTemplateCorrelationIdsChange}
        >
          <option key="<none>" value="">Select an Accuracy Template</option>
          {correlationIds.sort().map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </QESSelect>
      );

      renderAccuracySpecialtyDefinitionList = (
        <QESSelect
          label="Accuracy Specialty Definition"
          className="form-control"
          value={this.props.createProjectModel.accuracySpecialtyDefinitionId || ""}
          testId="create-project-step-template__accuracy-specialty-definition-list-select"
          onChange={this.props.handleAccuracySpecialtyDefChange}
        >
          <option key="<none>" value="">Select an Accuracy Specialty Definition</option>
          {this.props.accuracySpecialtyDefinitions.sort((a, b) =>
            a.name > b.name ? 1 : -1).map((option) => (
              <option key={option.specialtyDefinitionId} value={option.specialtyDefinitionId}>{option.name}</option>
            ))}
        </QESSelect>);
    }

    // A template must be chosen before displaying anything about a codeMap
    if (this.props.projectAccessCodeMapSets && this.props.projectAccessCodeMapSets.length > 0) {
      renderAccessCodeMapsList = (
        <QESSelect
          label="Access Region to Classification Code Map"
          className="form-control"
          onChange={this.props.handleAccessCodeMapSetChange}
          testId="create-project-step-template__access-region-to-classification-code-map-select"
          value={this.props.createProjectModel.accessCodeMapSetId ? this.props.createProjectModel.accessCodeMapSetId : ""}
        >
          {this.props.projectAccessCodeMapSets[0].codeMapSetId === this.props.accessCodeMapDefaultSetId ? this.props.createProjectModel.accessCodeMapSetId : <option value="0"> Select an Access Region to Classification Code Map</option>}
          {(this.sortArrayAfterFirstItem(this.props.projectAccessCodeMapSets) as CodeMapSet[]).map((set) => (
            set.codeMapSetVersions.some((setversion) => setversion.codeMapSetVersionStatus === "activated") ?
              <option key={set.codeMapSetId} value={set.codeMapSetId}>{set.resource.name}</option> :
              <option key={set.codeMapSetId} value={set.codeMapSetId} disabled={true}>{set.resource.name} - No Activated Versions</option>
          ))}
        </QESSelect>
      );
    }

    if (this.props.specialtyCodeMapSets && this.props.specialtyCodeMapSets.length > 0) {
      renderNetworkSpecialtyCodeMapsList = (
        <QESSelect
          label="Network Specialty Code Map"
          className="form-control"
          onChange={this.props.handleNetworkSpecialtyCodeMapSetChange}
          testId="create-project-step-template__network-specialty-code-maps-list-select"
          value={this.props.createProjectModel.networkSpecialtyCodeMapSetId ? this.props.createProjectModel.networkSpecialtyCodeMapSetId : 0}
        >
          <option value={null}>Select a Network Specialty Code Map</option>
          {this.props.specialtyCodeMapSets.sort((a, b) => a.resource.name > b.resource.name ? 1 : -1).map((set) => (
            set.codeMapSetVersions.some((sv) => sv.codeMapSetVersionStatus === "activated") ?
              <option key={set.codeMapSetId} value={set.codeMapSetId}>{set.resource.name}</option> :
              <option key={set.codeMapSetId} value={set.codeMapSetId} disabled={true}>{set.resource.name} - No Activated Versions</option>
          ))}
        </QESSelect>
      );

      renderMarketSpecialtyCodeMapsList = (
        <QESSelect
          label="Market Specialty Code Map"
          className="form-control"
          onChange={this.props.handleMarketSpecialtyCodeMapSetChange}
          testId="create-project-step-template__market-specialty-code-maps-list-select"
          value={this.props.createProjectModel.marketSpecialtyCodeMapSetId ? this.props.createProjectModel.marketSpecialtyCodeMapSetId : 0}
        >
          <option value={null}>Select a Market Specialty Code Map</option>
          {this.props.specialtyCodeMapSets.sort((a, b) => a.resource.name > b.resource.name ? 1 : -1).map((set) => (
            set.codeMapSetVersions.some((sv) => sv.codeMapSetVersionStatus === "activated") ?
              <option key={set.codeMapSetId} value={set.codeMapSetId}>{set.resource.name}</option> :
              <option key={set.codeMapSetId} value={set.codeMapSetId} disabled={true}>{set.resource.name} - No Activated Versions</option>
          ))}
        </QESSelect>
      );
    }

    const renderSpecialtyToQdpCodeMapsList = (
      <QESSelect
        label="Specialty to BDX UID Code Map"
        className="form-control"
        testId="create-project-step-template__specialty-to-qdp-code-maps-list-select"
        onChange={this.props.handleSpecialtyToQdpCodeMapSetChange}
        value={this.props.createProjectModel.accuracySpecialtyToQdpCodeMapSetId ? this.props.createProjectModel.accuracySpecialtyToQdpCodeMapSetId : ""}
      >
        <option value={null}>Select a Specialty to BDX UID Code Map</option>
        {this.props.specialtyToQdpCodeMapSets.sort((a, b) => a.resource.name > b.resource.name ? 1 : -1).map((set) => (
          set.codeMapSetVersions.some((sv) => sv.codeMapSetVersionStatus === "activated") ?
            <option key={set.codeMapSetId} value={set.codeMapSetId}>{set.resource.name}</option> :
            <option key={set.codeMapSetId} value={set.codeMapSetId} disabled={true}>{set.resource.name} - No Activated Versions</option>
        ))}
      </QESSelect>
    );

    const shouldRenderCodeMapDropdowns =
      this.props.specialtyCodeMapSets &&
      this.props.specialtyCodeMapSets.length > 0 &&
      !!this.props.createProjectModel.adequacyTemplateCorrelationId &&
      this.props.createProjectModel.adequacyTemplateCorrelationId !== "0";

    return (
      <WizardStep {...this.props} heading="Create a New Project" subheading="Select a template..." baseUrl={this.props.baseUrl} prevUrl={(this.props.currentIndex - 1).toString()} nextUrl={(this.props.currentIndex + 1).toString()} progressList={this.props.progressList} isNextDisabled={this.handleIsNextDisabled()}>
        <RoleRestricted allowedRoles={[Roles.ACCURACY_LICENSE_TIER_1]}>
          {hasAccuracyTemplates && !this.props.isZipProject && (
            <>
              {this.props.specialtyDefinitionsMissing && (
                <QESAlert type="danger" classNames="mb-2" persist={true}>
                  Could not find Specialty Definitions in the database
                </QESAlert>
              )}

              {this.props.authorizedInsurancesErred && (
                <QESAlert type="danger" classNames="mb-2" persist={true}>
                  Unable to load available accuracy insurance plans at this time.
                </QESAlert>
              )}

              <div className="form-group row">
                <div className="col-4 offset-2 d-flex">
                  <QESCheckbox
                    containerClassName="checkbox align-self-center"
                    label="Accuracy Analysis"
                    checked={this.props.useAccuracy && !this.props.isZipProject}
                    onChange={this.props.handleUseAccuracyChange}
                    testId="create-project-step-template__accuracy-analysis-checkbox"
                  />
                </div>
              </div>

              {this.props.accuracyIsLoading && (
                <div className="col-4 offset-2 d-flex">
                  <LoadingSpinner />
                </div>
              )}

              {this.props.useAccuracy && (
                <>
                  <div className="form-group row">
                    <div className="col-4 offset-2">
                      {renderAccuracyTemplateCollectionsList}
                    </div>
                  </div>

                  <div className="form-group row">
                    <div className="col-4 offset-2">
                      {renderAccuracyTemplateCorrelationIdsList}
                    </div>
                  </div>
                </>
              )}

              {this.props.useAccuracy && this.props.specialtyToQdpCodeMapSets && this.props.specialtyToQdpCodeMapSets.length > 0 &&
                <div className="form-group row">
                  <div className="col-4 offset-2">
                    {renderSpecialtyToQdpCodeMapsList}
                  </div>
                </div>
              }

              {this.props.useAccuracy && !this.props.useAdequacy && (
                <>
                  <div className="form-group row">
                    <div className="col-4 offset-2">
                      {renderAccuracySpecialtyDefinitionList}
                    </div>
                  </div>
                </>
              )}

              {this.props.useAccuracy && !this.props.useAdequacy
                && this.props.specialtyCodeMapSets && this.props.specialtyCodeMapSets.length > 0 && (
                  <>
                    <div className="form-group row">
                      <div className="col-4 offset-2">
                        {renderMarketSpecialtyCodeMapsList}
                      </div>
                    </div>
                  </>
                )}

              {
                this.props.useAccuracy && (
                  <div className="form-group row">
                    <div className="col-4 offset-2">
                      {renderAuthorizedInsurancesList}
                    </div>
                  </div>
                )
              }

              <div className="form-group row">
                <div className="col-4 offset-2">
                  <hr />
                </div>
              </div>

              <div className="form-group row">
                <div className="col-4 d-flex offset-2">
                  <QESCheckbox
                    containerClassName="checkbox align-self-center"
                    label="Adequacy Analysis"
                    checked={this.props.useAdequacy}
                    testId="create-project-step-template__adequacy-analysis-checkbox"
                    onChange={this.props.handleUseAdequacyChange}
                  />
                </div>
              </div>
            </>
          )}
        </RoleRestricted>

        {this.props.useAdequacy && (
          <>
            <div className="form-group row">
              <div className="col-4 offset-2">
                {renderProjectTemplateCollectionsList}
              </div>
            </div>
            <div className="form-group row">
              <div className="col-4 offset-2">
                {renderProjectTemplateCorrelationIdsList}
              </div>
            </div>
            <div className="form-group row">
              <div className="col-4 offset-2">
                {renderTimeDistanceCalculationTypeList}
              </div>
            </div>
          </>
        )}

        {this.props.projectAccessCodeMapSets && this.props.projectAccessCodeMapSets.length > 0 &&
          <div className="form-group row">
            <div className="col-4 offset-2">
              {renderAccessCodeMapsList}
            </div>
          </div>
        }

        {shouldRenderCodeMapDropdowns && (
          <>
            {this.props.showNetworkSpecialtyCodeMap && !this.props.isZipProject && (
              <div className="form-group row">
                <div className="col-4 offset-2">
                  {renderNetworkSpecialtyCodeMapsList}
                </div>
              </div>
            )}

            <div className="form-group row">
              <div className="col-4 offset-2">
                {renderMarketSpecialtyCodeMapsList}
              </div>
            </div>
          </>
        )}

        {this.state.sortedSpecialtyGroups && this.state.sortedSpecialtyGroups.length > 0 && (
          <div className="form-group row">
            <div className="col-8">
              <SpecialtyGroupsTable
                canEdit={true}
                excludedSpecialtyGroupCodes={this.props.createProjectModel.excludedSpecialtyGroupCodes}
                handleSpecialtyGroupsChange={this.props.handleSpecialtyGroupsChange}
                handleTelehealthSpecialtyGroupsChange={this.props.handleTelehealthSpecialtyGroupsChange}
                specialtyGroups={this.state.sortedSpecialtyGroups}
                telehealthSpecialtyGroupCodes={this.props.createProjectModel.telehealthSpecialtyGroupCodes}
              />
            </div>
          </div>
        )}
      </WizardStep>
    );
  }

  private sortArrayAfterFirstItem = (arr: Array<MemberSetModel | CodeMapSet>): Array<MemberSetModel | CodeMapSet> => {
    let newArr = arr;
    const firstItem = newArr.shift();
    newArr = newArr.sort((a, b) => a.resource.name > b.resource.name ? 1 : -1);
    newArr.unshift(firstItem);
    return newArr;
  }

  private handleIsNextDisabled() {
    //user must select at least adequacy or accuracy
    if (!this.props.createProjectModel.accuracyTemplateCollectionId &&
      !this.props.createProjectModel.adequacyTemplateCollectionId) {
      return true;
    }

    //user selected Accuracy template collection but no accuracy template
    if (this.props.createProjectModel.accuracyTemplateCollectionId &&
      !this.props.createProjectModel.accuracyTemplateCorrelationId) {
      return true;
    }

    //user selected Accuracy template collection but no code map set
    if (this.props.createProjectModel.accuracyTemplateCollectionId &&
      !this.props.createProjectModel.accuracySpecialtyToQdpCodeMapSetId) {
      return true;
    }

    //user selected adequacy template collection but no adequacy template
    if (this.props.createProjectModel.adequacyTemplateCollectionId &&
      !this.props.createProjectModel.adequacyTemplateCorrelationId) {
      return true;
    }

    //if accuracy-only project, ensure specialty definition is selected
    if (this.props.createProjectModel.accuracyTemplateCollectionId &&
      !this.props.createProjectModel.adequacyTemplateCollectionId &&
      !this.props.createProjectModel.accuracySpecialtyDefinitionId &&
      !this.state.sortedSpecialtyGroups ||
      this.state.sortedSpecialtyGroups.length === this.props.createProjectModel.excludedSpecialtyGroupCodes.length) {
      return true;
    }

    //if adequacy is selected, validate the rest of its fields
    if (this.props.createProjectModel.adequacyTemplateCollectionId &&
      ((this.props.projectAccessCodeMapSets &&
        this.props.projectAccessCodeMapSets.length > 0 &&
        !this.props.createProjectModel.accessCodeMapSetId) ||
        !this.state.sortedSpecialtyGroups ||
        this.state.sortedSpecialtyGroups.length === this.props.createProjectModel.excludedSpecialtyGroupCodes.length ||
        !this.props.createProjectModel.timeDistanceCalculatorType || this.props.createProjectModel.timeDistanceCalculatorType === undefined)) {
      return true;
    }
  }
}
